import * as yup from 'yup';
import { CustomerType } from './enum';

import countries from 'resources/addresses';
import { isRelationshipNeedsPreferredContact } from '../ConsentForm/schema';
import { validateDateSchema, validatePostCode, validateTextSchema } from 'utils/commonSchema';

yup.setLocale({
  mixed: {
    oneOf: '',
    required: '',
  },
});

const textFieldMax = 50;
const textFieldMin = 1;
const addressMax = 225;
const stateMax = 20;
const phoneNumberMax = 20;
const emailMax = 100;
const otherLangMin = 3;
const emailRegex =
  /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
const phoneNumberRegex =
  /^(([0-9]{6,20})|([+]?[(]?(([2-9][0-9])|([2-8][0-9][0-9]))[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}))$/;

const postalAddressErrorMsgMin = 'Remember to include your unit number as well as your street number';

export const phoneNumberSchema = (excludeEmptyString = true) =>
  yup
    .string()
    .matches(phoneNumberRegex, { excludeEmptyString, message: 'Please enter a valid phone number' })
    .max(phoneNumberMax);

export const emailSchema = ({ excludeEmptyString = true, required = true }) => {
  const schema = yup
    .string()
    .matches(emailRegex, { excludeEmptyString, message: 'Please enter a valid email address' })
    .max(emailMax);

  return required ? schema.required('Please enter a valid email address') : schema;
};

export const insuredDetailSchemaObject = {
  insuredIndOrBus: yup.string().ensure().oneOf(Object.values(CustomerType)).required(),

  customerFirstName: yup
    .string()
    .nullable()
    .when('insuredIndOrBus', {
      is: CustomerType.INDIVIDUAL,
      then: () => validateTextSchema({ minLen: textFieldMin, maxLen: textFieldMax, required: true }),
    }),

  customerLastName: yup
    .string()
    .nullable()
    .when('insuredIndOrBus', {
      is: CustomerType.INDIVIDUAL,
      then: () => validateTextSchema({ minLen: textFieldMin, maxLen: textFieldMax, required: true }),
    }),

  contactFirstName: yup
    .string()
    .nullable()
    .when('insuredIndOrBus', {
      is: CustomerType.BUSINESS,
      then: () => validateTextSchema({ minLen: textFieldMin, maxLen: textFieldMax, required: true }),
    }),

  contactLastName: yup
    .string()
    .nullable()
    .when('insuredIndOrBus', {
      is: CustomerType.BUSINESS,
      then: () => validateTextSchema({ minLen: textFieldMin, maxLen: textFieldMax, required: true }),
    }),
  dob: yup
    .date()
    .when('insuredIndOrBus', {
      is: CustomerType.INDIVIDUAL,
      then: () => validateDateSchema({ hasMinDate: true }),

      // .max(new Date(new Date().setHours(23, 59, 59))),
    })
    .when('insuredIndOrBus', {
      is: CustomerType.BUSINESS,
      then: () => yup.date().nullable(),
    }),
  gender: yup
    .string()
    .nullable()
    .when('insuredIndOrBus', {
      is: CustomerType.INDIVIDUAL,
      then: () => yup.string().oneOf(['Male', 'Female', 'Non-binary', 'Prefer not to say']).required(),
    }),
};

const contactDetailSchemaObject = {
  cEmailAddress: yup
    .string()
    // .email()
    .when('iContactPreferedMethod', {
      is: 'email',
      then: () => emailSchema({ excludeEmptyString: false }),
      otherwise: () => emailSchema({ excludeEmptyString: true, required: false }),
    }),
  iContactPreferedMethod: yup.string().when(['contactType', 'relationship'], {
    is: (contactType: string, relationship: string) =>
      contactType === 'no' || !isRelationshipNeedsPreferredContact(relationship),
    then: () => yup.string().oneOf(['email', 'post', 'phone']).required(),
  }),
  addressType: yup.string().oneOf(['yes', 'no']).nullable().required(),

  pStreetAddress: yup.string().when(['isManualAddressPhy', 'addressType'], {
    is: (isManualAddressPhy: boolean, addressType: string) => addressType === 'no' && isManualAddressPhy,
    then: () => validateTextSchema({ minLen: textFieldMin, maxLen: addressMax, required: true }),
  }),

  pSuburb: yup.string().when(['isManualAddressPhy', 'addressType'], {
    is: (isManualAddressPhy: boolean, addressType: string) => addressType === 'no' && isManualAddressPhy,
    then: () => validateTextSchema({ minLen: textFieldMin, maxLen: addressMax, required: true }),
  }),

  pState: yup.string().when(['isManualAddressPhy', 'addressType'], {
    is: (isManualAddressPhy: boolean, addressType: string) => addressType === 'no' && isManualAddressPhy,
    then: () => validateTextSchema({ minLen: textFieldMin, maxLen: stateMax, required: true }),
  }),
  pCountry: yup
    .string()
    .nullable()
    .when(['isManualAddressPhy', 'addressType'], {
      is: (isManualAddressPhy: boolean, addressType: string) => addressType === 'no' && isManualAddressPhy,
      then: () => yup.string().oneOf(countries, 'Country is a required field').required().label('Country'),
    }),

  pPostcode: yup.string().test('postalCode', 'Invalid postcode', function (postCode) {
    const { isManualAddressPhy, pState, addressType, pCountry } = this.parent;
    if (isManualAddressPhy && addressType === 'no' && typeof postCode === 'string') {
      return validatePostCode(pCountry)(pState)(postCode);
    }
    return true;
  }),

  cPostalAddress: yup
    .string()
    .nullable()
    .when(['isManualAddressPhy', 'addressType'], {
      is: (isManualAddressPhy: boolean, addressType: string) => !isManualAddressPhy && addressType === 'no',
      then: () => yup.string().required(postalAddressErrorMsgMin).min(4, postalAddressErrorMsgMin),
    }),

  isPhyAddressValid: yup.boolean().when(['isManualAddressPhy', 'addressType'], {
    is: (isManualAddressPhy: boolean, addressType: string) => !isManualAddressPhy && addressType === 'no',
    then: () => yup.boolean().required().oneOf([true]),
  }),

  rCountry: yup
    .string()
    .nullable()
    .when('isManualAddressRes', {
      is: true,
      then: () => yup.string().oneOf(countries, 'Country is a required field').required().label('Country'),
    }),

  rState: yup.string().when('isManualAddressRes', {
    is: true,
    then: () => validateTextSchema({ minLen: textFieldMin, maxLen: stateMax, required: true }),
  }),

  rStreetAddress: yup.string().when('isManualAddressRes', {
    is: true,
    then: () => validateTextSchema({ minLen: textFieldMin, maxLen: addressMax, required: true }),
  }),

  rSuburb: yup.string().when('isManualAddressRes', {
    is: true,
    then: () => validateTextSchema({ minLen: textFieldMin, maxLen: addressMax, required: true }),
  }),

  rPostcode: yup.string().test('postalCode', 'Invalid postcode', function (postCode) {
    const { rState, isManualAddressRes, rCountry } = this.parent;
    if (isManualAddressRes && typeof postCode === 'string') {
      return validatePostCode(rCountry)(rState)(postCode);
    }
    return true;
  }),

  haveOneValidNumber: yup.boolean().when('iContactPreferedMethod', {
    is: 'phone',
    then: () => yup.boolean().oneOf([true]),
  }),

  mPhoneNumber: phoneNumberSchema(),

  hPhoneNumber: phoneNumberSchema(),

  oPhoneNumber: phoneNumberSchema(),

  residentialAddress: yup
    .string()
    .nullable()
    .when(['isManualAddressRes'], {
      is: (isManualAddressRes: boolean) => !isManualAddressRes,
      then: () => yup.string().required(postalAddressErrorMsgMin).min(4, postalAddressErrorMsgMin),
    }),

  isResAddressValid: yup.boolean().when(['isManualAddressRes'], {
    is: (isManualAddressRes: boolean) => !isManualAddressRes,
    then: () => yup.boolean().required().oneOf([true]),
  }),
};

const otherDetailsSchemaObject = {
  // other details
  isVulnerableCustomer: yup.string().ensure().oneOf(['yes', 'no']),

  vulnerabilityReason: yup
    .string()
    .nullable()
    .when('isVulnerableCustomer', {
      is: 'yes',
      then: () =>
        yup
          .string()
          .oneOf([
            'Aboriginal or Torres Strait Islander status',
            'Age',
            'Cultural background',
            'Disability',
            'Family violence',
            'Financial distress',
            'Language barriers',
            'Mental health conditions',
            'Physical health conditions',
            'Remote location',
          ])
          .required(),
    }),

  isLanguageInterpreter: yup
    .string()
    .nullable()
    .when(['isVulnerableCustomer', 'vulnerabilityReason'], {
      is: (isVulnerableCustomer: string, vulnerabilityReason: string) =>
        isVulnerableCustomer === 'yes' && vulnerabilityReason === 'Language barriers',
      then: () => yup.string().oneOf(['yes', 'no']).required(),
    }),

  language: yup
    .string()
    .nullable()
    .when('isLanguageInterpreter', {
      is: 'yes',
      then: () =>
        yup
          .string()
          .oneOf([
            'Arabic',
            'Cantonese',
            'Greek',
            'Hindi',
            'Italian',
            'Mandarin',
            'Punjabi',
            'Spanish',
            'Vietnamese',
            'Other',
          ])
          .required(),
    }),

  otherLanguage: yup
    .string()
    .nullable()
    .when(['isVulnerableCustomer', 'vulnerabilityReason', 'isLanguageInterpreter', 'language'], {
      is: (
        isVulnerableCustomer: string,
        vulnerabilityReason: string,
        isLanguageInterpreter: string,
        language: string
      ) =>
        isVulnerableCustomer === 'yes' &&
        vulnerabilityReason === 'Language barriers' &&
        isLanguageInterpreter === 'yes' &&
        language === 'Other',
      then: () => validateTextSchema({ minLen: otherLangMin, maxLen: textFieldMax, required: true }),
    }),

  customerDesc: validateTextSchema({ minLen: 0, maxLen: 4000, required: false }),

  businessName: yup
    .string()
    .nullable()
    .when('insuredIndOrBus', {
      is: CustomerType.BUSINESS,
      then: () => validateTextSchema({ minLen: textFieldMin, maxLen: emailMax, required: true }),
    }),

  isSmallBusiness: yup
    .string()
    .nullable()
    .when('insuredIndOrBus', {
      is: CustomerType.BUSINESS,
      then: () => yup.string().required(),
    }),
};

export const insuredDetailSchema = yup.object().shape(insuredDetailSchemaObject);
export const contactDetailSchema = yup.object().shape(contactDetailSchemaObject);
export const otherDetailSchema = yup.object().shape(otherDetailsSchemaObject);

const validationSchema = yup.object().shape({
  ...insuredDetailSchemaObject,
  ...contactDetailSchemaObject,
  ...otherDetailsSchemaObject,
});

export default validationSchema;
